import moment from "moment";

import { Diagnosis } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Diagnosis";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { RemoveButton } from "@/Common/Components/Buttons/RemoveButton";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { MedicalItemsTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { Relationship } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { DiagnosisSearchAutocomplete } from "@/Patient/Components/Diagnosis/DiagnosisSearchAutocomplete";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { LifeStageModeEnum } from "@/Patient/Models/LifeStageModeEnum";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { MedicalItem, MedicalItemModel } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemFamilyHistory } from "@/Patient/Models/MedicalSummary/MedicalItemFamilyHistory";
import { QualifiedDetail } from "@/Patient/Models/MedicalSummary/MedicalItemProblemAndCondition";
import { BaseMedicalItemsView, IItemDetailsRenderFunction, IListItemRenderFunction, IMedicalItemsViewProps } from "./BaseMedicalItemsView";
import { BaseMedicalItemsViewState } from "./BaseMedicalItemsViewState";
import { LifeStageRow } from "./LifeStageRow";

class MedicalItemsFamilyHistoryViewState extends BaseMedicalItemsViewState {
    public IsNewEMRTemplate: boolean;

    public Description: string;
    public QualifiedDetails: QualifiedDetail[];
    public Diagnostic: string;
    public AvailableRelationships: FlatLookupView[];
    public SelectedRelationshipLookupId: string;

    public OtherRelationShipLookupId: string = cacheStore
        .FindLookupByLookupCode(Relationship.Other, Relationship.Code).LookupID;
    public OtherRelationshipSpecification: string;

    public Treatment: string;
    public Notes: string;

    public Id: string;
    public DisplayId: string;

    constructor(s: MedicalItemsFamilyHistoryViewState = null) {
        super(s);

        if (s != null) {
            this.IsNewEMRTemplate = s.IsNewEMRTemplate;
            this.Description = s.Description;
            this.QualifiedDetails = s.QualifiedDetails;
            this.AvailableRelationships = s.AvailableRelationships;
            this.SelectedRelationshipLookupId = s.SelectedRelationshipLookupId;
            this.Treatment = s.Treatment;
            this.Notes = s.Notes;
            this.OtherRelationshipSpecification = s.OtherRelationshipSpecification;
            this.Id = s.Id;
        }
        else {
            this.AvailableRelationships = MedicalItemsFamilyHistoryViewState.GetOrderedFamilyRelationhships();
            this.SelectedRelationshipLookupId = this.AvailableRelationships.filter(x => x.LookupID != "")[0].LookupID;
            this.QualifiedDetails = [];
        }
    }

    private static GetOrderedFamilyRelationhships(): FlatLookupView[] {
        const result: FlatLookupView[] = [];

        result.push(cacheStore.FindLookupByLookupCode(Relationship.Mother, Relationship.Code));
        result.push(cacheStore.FindLookupByLookupCode(Relationship.Father, Relationship.Code));
        result.push(cacheStore.FindLookupByLookupCode(Relationship.Daughter, Relationship.Code));
        result.push(cacheStore.FindLookupByLookupCode(Relationship.Son, Relationship.Code));
        result.push(cacheStore.FindLookupByLookupCode(Relationship.Sister, Relationship.Code));
        result.push(cacheStore.FindLookupByLookupCode(Relationship.Brother, Relationship.Code));
        result.push(cacheStore.FindLookupByLookupCode(Relationship.Cousin, Relationship.Code));
        result.push(cacheStore.FindLookupByLookupCode(Relationship.MaternalGrandFather, Relationship.Code));
        result.push(cacheStore.FindLookupByLookupCode(Relationship.MaternalGrandMother, Relationship.Code));
        result.push(cacheStore.FindLookupByLookupCode(Relationship.MaternalSibling, Relationship.Code));
        result.push(cacheStore.FindLookupByLookupCode(Relationship.PaternalGrandFather, Relationship.Code));
        result.push(cacheStore.FindLookupByLookupCode(Relationship.PaternalGrandMother, Relationship.Code));
        result.push(cacheStore.FindLookupByLookupCode(Relationship.PaternalSibling, Relationship.Code));
        // result.push(cacheStore.FindLookupByLookupCode(Relationship.Guardian, Relationship.Code));
        result.push(cacheStore.FindLookupByLookupCode(Relationship.Other, Relationship.Code));

        return result;
    }
}

class MedicalItemsFamilyHistoryView extends BaseMedicalItemsView<IMedicalItemsViewProps, MedicalItemsFamilyHistoryViewState> {
    public componentWillMount(): void {
        let s = null;
        let cacheId = this.getItemCacheId(this.props);
        if (cacheId != null) {
            s = patientStore.getMedicalItemCachedState(this.props.PatientContactId, cacheId);
        }
        if (s == null) {
            s = new MedicalItemsFamilyHistoryViewState();
        }
        this.setState(s);
    }

    public componentWillReceiveProps(nextProps: IMedicalItemsViewProps): void {
        // when the model changes, check if we have to render an item in edit mode, if so, we set the state so we can edit its values
        const model = nextProps.Model;

        if (model != this.props.Model) {
            const isEditing = this.isEditing(model);
            const medicalItems = model.get("MedicalItemsList") ? model.get("MedicalItemsList").toArray() : [];

            if (isEditing && medicalItems && medicalItems.length > 0) {
                const latestNewItem = MedicalItemModel.sortByCreationDateDesc(medicalItems.filter(x => isGuidNullOrUndefinedOrEmpty(x.Id))).firstOrDefault();

                if (latestNewItem != null) {
                    // Use the new MedicalItem
                    this.SetEditedItemState(latestNewItem);
                }
                else {
                    // modify the last item if Uncommitted
                    const latestMedicalItem = MedicalItemModel.sortByCreationDateDesc(medicalItems).firstOrDefault();

                    if (latestMedicalItem != null && !latestMedicalItem.IsCommitted)
                        this.SetEditedItemState(latestMedicalItem);
                }
            }
        }
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    public componentDidUpdate(): void {
        $(window).trigger("resize");
    }

    public componentWillUnmount(): void {
        patientStore.setMedicalItemCachedState(this.props.PatientContactId, this.state.Id, this.state);
    }

    public render(): JSX.Element {
        const self = this;
        const state = this.state;

        const listItemRenderFunction: IListItemRenderFunction = function(fam: MedicalItemFamilyHistory): JSX.Element {
            const lifestageString = PatientMedicalSummaryHelper.GetLifeStageString(fam);

            let relationShipString = MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(fam.RelationshipLookupId));
            if (fam.RelationshipLookupId == state.OtherRelationShipLookupId)
                relationShipString = relationShipString + " : " + fam.SpecifiedRelationship;

            return (
                <div className="med-note" key={"fam" + fam.Id}>
                    <div className="flex-container medical-note">
                        <div className="flx-col a">
                            <p className="ellipsis "><strong>{relationShipString}</strong></p>
                            <p className="ellipsis " style={{ "width": "100%" }}><strong>{fam.Problem}</strong></p>
                            {fam.EMRTemplateIsInvalid &&
                                <p className="red-text">{cacheStore.GetString("InvalidMedicalItemLabel")} </p>
                            }
                            <p className="ellipsis " style={{ "width": "100%" }}> {lifestageString}</p>
                        </div>
                    </div>
                </div>
            );
        };

        const itemDetailsRenderFunction: IItemDetailsRenderFunction = function(fam: MedicalItemFamilyHistory): JSX.Element {
            const lifestageString = PatientMedicalSummaryHelper.GetLifeStageString(fam);

            let relationShipString = MyleHelper.ReplaceEmptyString(cacheStore.GetLocalizedValueByLookupId(fam.RelationshipLookupId));

            if (fam.RelationshipLookupId == state.OtherRelationShipLookupId)
                relationShipString = relationShipString + " : " + fam.SpecifiedRelationship;

            return (
                <div className="medical-reaction-detail-box">
                    <table className="medical-reaction-details border" data-cy="medicalItemsFamilyHistory-details">
                        <tbody>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("DiagnosisDLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong>{MyleHelper.ReplaceEmptyString(fam.Problem)}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("ProblemDescriptionDLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong>{JSXHelper.BreakStringIntoParagraphs(MyleHelper.ReplaceEmptyString(fam.Description))} </strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("RelationshipLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong>{relationShipString}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("StartDateLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong data-cy="medicalItemsFamilyHistory-startDate">{MyleHelper.ReplaceEmptyString(lifestageString)}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("TreatmentLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong>{JSXHelper.BreakStringIntoParagraphs(MyleHelper.ReplaceEmptyString(fam.Treatment))}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("NotesLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong>{JSXHelper.BreakStringIntoParagraphs(MyleHelper.ReplaceEmptyString(fam.Notes))}</strong>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            );
        };

        const itemDetailsEditRenderFunction: IItemDetailsRenderFunction = (family: MedicalItemFamilyHistory): JSX.Element => {
            const diagnosticsRender = this.state.QualifiedDetails.map(x => (
                <div className="row" key={"famdiaag_" + x.Id}>
                    <div className="col-sm-12" style={{ "textAlign": "left" }}>
                        <strong>{x.Text}</strong>  <RemoveButton onRemove={() => this.mutateState(s => s.QualifiedDetails = s.QualifiedDetails.filter(qd => qd.Id !== x.Id))} />
                    </div>
                </div>
            ));

            const isDiagnosticsInputValid = (this.state.IsNewEMRTemplate && this.state.QualifiedDetails?.some(x => x.Text?.trim().length > 0))
                || this.state.Diagnostic?.trim().length > 0;

            return (
                <div className="medical-reaction-detail-box">
                    <table className="medical-problems-edit border">
                        <tbody>
                            <tr>
                                <td className="c1">
                                    <span className="label-error" />  <span>{cacheStore.GetString("TypeLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <SelectLookupControl
                                        IncludeEmptyChoice
                                        Value={this.state.SelectedRelationshipLookupId}
                                        AvailableValues={this.state.AvailableRelationships.map(x => x.ToComboboxItem())}
                                        Callback={lookupId => this.mutateState(s => s.SelectedRelationshipLookupId = lookupId)}
                                        dataCy="medicalItemsFamilyHistory-type"
                                    />
                                </td>
                            </tr>
                            {this.state.SelectedRelationshipLookupId == this.state.OtherRelationShipLookupId && (
                                <tr>
                                    <td className="c1">
                                        <span className="label-error" />  <span>{cacheStore.GetString("SpecifyLabel")}</span>
                                    </td>
                                    <td className="c2" colSpan={2}>
                                        <TextboxControl
                                            valid={this.state.OtherRelationshipSpecification?.trim().length > 0}
                                            className="form-control"
                                            value={this.state.OtherRelationshipSpecification}
                                            onChange={text => this.mutateState(s => s.OtherRelationshipSpecification = text)}
                                        />
                                    </td>
                                </tr>
                            )}
                            <tr>
                                <td className="c1">
                                    <span className="label-error" /> <span>{cacheStore.GetString("DiagnosisLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <DiagnosisSearchAutocomplete
                                        id="diagSearch"
                                        dataCy="medicalItemsFamilyHistory-diagnosis"
                                        valid={isDiagnosticsInputValid}
                                        debounce={400}
                                        onItemSelect={item => this.onDiagnosticTextSelected(item)}
                                        onBlur={text => this.onDiagnosticOnBlur(text)}
                                        onTextChange={text => this.mutateState(s => s.Diagnostic = text)}
                                        clearTextAfterSelect
                                        newValue={this.state.Diagnostic}
                                        siteId={this.props.Model.get("Encounter")?.SiteId ?? sessionStore.getCurrentEmployee().getActiveSite().getId()}
                                    />
                                </td>
                            </tr>

                            {family.IsNewEMRTemplate && (
                                <tr>
                                    <td className="c1" />
                                    <td className="c2" colSpan={2}>
                                        {diagnosticsRender}
                                    </td>
                                </tr>
                            )}

                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("ProblemDescriptionDLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <TextAreaResizable
                                        minHeightPx={100}
                                        id={"diag_descri" + (family.displayId ?? family.Id)}
                                        dataCy="medicalItemsFamilyHistory-description"
                                        text={this.state.Description}
                                        isDisabled={false}
                                        onChange={value => this.mutateState(s => s.Description = value)}
                                        resetTextOnIdChange
                                    />
                                </td>
                            </tr>

                            <LifeStageRow
                                lifeStageMode={this.state.LifeStageMode}
                                lifeStageAvailableAgeAtOnsetUnits={this.state.LifeStageAvailableAgeAtOnsetUnits}
                                lifeStageAgeSelectedAgeAtOnsetUnit={this.state.LifeStageAgeSelectedgeAtOnsetUnit}
                                lifeStageSelectedLifeStageLookupId={this.state.LifeStageSelectedLifestageLookupId}
                                lifeStageAvailableLifeStages={this.state.LifeStageAvailableLifestages}
                                lifeStageAgeAtOnset={this.state.LifeStageAgeAtOnset}
                                lifeStageDatePrecision={this.state.LifeStageDatePrecision}
                                lifeStageDateTextboxValue={this.state.LifeStageDateTextboxValue}
                                lifeStageStartDate={this.state.LifeStageStartDate}
                                lifeStageIsStartDateApprox={this.state.LifeStageIsStartDateApproximative}
                                useThreeCols={false}
                                onLifeStageChange={value => this.LifeStageChanged(value)}
                                onLifeStageDateChange={(date, precision) => this.LifeStageDateChanged(date, precision)}
                                onLifeStageModeClick={newMode => this.LifeStageModeChanged(newMode)}
                                onLifeStageDateApproxChange={() => this.LifeStageDateApproxChange()}
                                onLifeStageDateTodayClick={() => this.LifeStageDateTodayClicked()}
                                onLifeStageAgeChange={value => this.LifeStageAgeChange(value)}
                                onLifeStageAgeUnitChange={value => this.LifeStageAgeUnitChange(value)}
                            />

                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("TreatmentLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <TextAreaResizable
                                        minHeightPx={100}
                                        id={"diag_treat" + (family.displayId ? family.displayId : family.Id)}
                                        dataCy="medicalItemsFamilyHistory-treatment"
                                        text={this.state.Treatment}
                                        onChange={value => this.mutateState(s => s.Treatment = value)}
                                        resetTextOnIdChange
                                    />
                                </td>
                            </tr>

                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("NotesLabel")}</span>
                                </td>
                                <td className="c2" colSpan={2}>
                                    <TextAreaResizable
                                        minHeightPx={100}
                                        id={"diag_notes" + (family.displayId ? family.displayId : family.Id)}
                                        dataCy="medicalItemsFamilyHistory-notes"
                                        text={this.state.Notes}
                                        onChange={value => this.mutateState(s => s.Notes = value)}
                                        resetTextOnIdChange
                                    />
                                </td>
                            </tr>

                        </tbody>
                    </table>
                </div>
            );
        };

        return (
            <BaseMedicalItemsView
                PatientContactId={this.props.PatientContactId}
                Model={this.props.Model}
                DetailsTitle={cacheStore.GetString("CppLabel") + " : " + cacheStore.GetString("FamilyHistoryMLabel").toTitleCase()}
                ListItemRenderFunction={listItemRenderFunction}
                MedicalItemRenderFunction={itemDetailsRenderFunction}
                MedicalItemEditRenderFunction={itemDetailsEditRenderFunction}
                SaveFunction={async () => await this.Save(false)}
                SavePlusFunction={async () => await this.SavePlus()}
                emrType={MedicalItemTypeEnum.FamilyHistory}
                CheckIfSaveEnabledFunction={self.CheckIfSaveIsEnabled.bind(self)}
                toSearchableStringFunction={(items: MedicalItem[]) => self.searchMedicalItem(items)}
                showFavorites={this.props.showFavorites}
            />
        );
    }

    private SetEditedItemState(medItem: MedicalItem): void {
        let item = medItem as MedicalItemFamilyHistory;

        if (!item || item.IsCollapsed || item.IsCommitted)
            return;

        let isSameItem = item.displayId == this.state.DisplayId && item.Id == this.state.Id;
        if (isSameItem)
            return;

        const s = new MedicalItemsFamilyHistoryViewState(this.state);
        s.Id = item.Id;
        s.DisplayId = item.displayId;
        if (item.IsNewEMRTemplate) {
            // we have a new medical item / emr template : contol behaves in "multiple creation mode"...
            s.Diagnostic = "";
            s.QualifiedDetails = [];
            s.Treatment = "";
            s.Description = "";
            s.SelectedRelationshipLookupId = s.AvailableRelationships.filter(x => x.LookupID != "")[0].LookupID;
        }
        else {
            // we are modifying an already saved medical item, control behaves normally
            s.Diagnostic = item.Problem;
            s.Treatment = item.Treatment;
            s.Description = item.Description;
            s.SelectedRelationshipLookupId = item.RelationshipLookupId != null ? item.RelationshipLookupId : "";
        }

        s.OtherRelationshipSpecification = item.SpecifiedRelationship;

        s.Notes = item.Notes;

        s.IsNewEMRTemplate = item.IsNewEMRTemplate;
        s.LifeStageStartDate = item.LifeStageDate;
        s.LifeStageIsStartDateApproximative = !(item.LifeStagePrecisionDate == LifeStageDatePrecisionEnum.Day);
        s.LifeStageDatePrecision = item.LifeStagePrecisionDate;
        s.LifeStageDateTextboxValue = s.LifeStageStartDate != null ? PatientMedicalItemsHelper.GetLifeStageDateString(s.LifeStageStartDate, item.LifeStagePrecisionDate) : "";

        s.LifeStageAgeAtOnset = item.LifeStageAge;
        s.LifeStageMode = item.LifeStageMode != null ? item.LifeStageMode : s.LifeStageMode;
        s.LifeStageSelectedLifestageLookupId = item.LifeStageStatusLookupId != null ? item.LifeStageStatusLookupId : s.LifeStageSelectedLifestageLookupId;
        s.LifeStageAgeSelectedgeAtOnsetUnit = item.LifeStageAgeStatusLookupId != null ? item.LifeStageAgeStatusLookupId : s.LifeStageAgeSelectedgeAtOnsetUnit;

        this.setState(s);
    }

    private onDiagnosticTextSelected(item: Diagnosis): void {
        const siteModel = sessionStore.getCurrentEmployee().getActiveSite();
        const displayLocal = item.getLocaleFromSite(siteModel);

        this.mutateState(s => {
            if (s.IsNewEMRTemplate) {
                const newDiag = new QualifiedDetail();
                newDiag.Text = item.getName(displayLocal);
                newDiag.Id = item.Id;
                s.QualifiedDetails.push(newDiag);
                s.Diagnostic = "";
            }
            else {
                s.Diagnostic = item.getName(displayLocal);
            }
        });
    }

    private onDiagnosticOnBlur(text: string): void {
        if (text?.trim().length > 0) {
            this.mutateState(s => {
                if (s.IsNewEMRTemplate) {
                    const newDiag = new QualifiedDetail();
                    newDiag.Text = text;
                    newDiag.Id = GuidHelper.NewGuid();
                    s.QualifiedDetails.push(newDiag);
                    s.Diagnostic = "";
                }
                else {
                    s.Diagnostic = text;
                }
            });
        }
    }

    private LifeStageModeChanged(newMode: LifeStageModeEnum): void {
        const s = new MedicalItemsFamilyHistoryViewState(this.state);
        s.LifeStageMode = newMode;

        if (s.LifeStageMode == LifeStageModeEnum.Age) {
            // s.LifeStageDatePrecision = LifeStageDatePrecisionEnum.MonthYear;
            s.LifeStageAgeSelectedgeAtOnsetUnit = cacheStore.FindLookupByLookupCode("year", "DayYear").LookupID;
            s.LifeStageAgeAtOnset = undefined;
            s.LifeStageIsStartDateApproximative = false;
        }
        else if (s.LifeStageMode == LifeStageModeEnum.Date) {
            s.LifeStageIsStartDateApproximative = s.LifeStageDatePrecision != LifeStageDatePrecisionEnum.Day;
        }

        this.setState(s);
    }

    private LifeStageDateApproxChange(): void {
        if (this.state && this.state.LifeStageDatePrecision == LifeStageDatePrecisionEnum.MonthYear || this.state.LifeStageDatePrecision == LifeStageDatePrecisionEnum.Year)
            return;

        const s = new MedicalItemsFamilyHistoryViewState(this.state);
        s.LifeStageIsStartDateApproximative = !s.LifeStageIsStartDateApproximative;
        s.LifeStageDatePrecision = s.LifeStageIsStartDateApproximative ? LifeStageDatePrecisionEnum.DayApprox : LifeStageDatePrecisionEnum.Day;

        this.setState(s);
    }

    private LifeStageDateChanged(date: DateTimeWithPrecision, precision: LifeStageDatePrecisionEnum = LifeStageDatePrecisionEnum.Day): void {
        const s = new MedicalItemsFamilyHistoryViewState(this.state);

        if (date.value) {
            s.LifeStageStartDate = moment(date.value).toDate();

            if (precision == LifeStageDatePrecisionEnum.DayApprox || precision == LifeStageDatePrecisionEnum.Day) {
                s.LifeStageDatePrecision = (this.state.LifeStageIsStartDateApproximative == true) ?
                    LifeStageDatePrecisionEnum.DayApprox :
                    LifeStageDatePrecisionEnum.Day;

                s.LifeStageDateTextboxValue = moment(date.value).format("YYYY-MM-DD"); // always iso 900somthing format
            }
            else {
                s.LifeStageDatePrecision = precision;
                s.LifeStageDateTextboxValue = PatientMedicalItemsHelper.GetLifeStageDateString(date.value, precision);
                s.LifeStageIsStartDateApproximative = true;
            }

            s.LifeStageMode = LifeStageModeEnum.Date;
        }
        else if (IsNullOrWhiteSpace(date.text)) {
            s.LifeStageStartDate = null;
            s.LifeStageDateTextboxValue = "";
            s.LifeStageIsStartDateApproximative = false;
        }

        this.setState(s);
    }

    private LifeStageDateTodayClicked(): void {
        const s = new MedicalItemsFamilyHistoryViewState(this.state);
        s.LifeStageDateTextboxValue = moment(Date.now()).format("YYYY-MM-DD");
        s.LifeStageStartDate = moment(Date.now()).toDate();
        s.LifeStageMode = LifeStageModeEnum.Date;
        s.LifeStageDatePrecision = (this.state.LifeStageIsStartDateApproximative == true) ?
            LifeStageDatePrecisionEnum.DayApprox :
            LifeStageDatePrecisionEnum.Day;

        this.setState(s);
    }

    private LifeStageChanged(lookupId: string): void {
        const s = new MedicalItemsFamilyHistoryViewState(this.state);
        s.LifeStageSelectedLifestageLookupId = lookupId;
        s.LifeStageMode = LifeStageModeEnum.LifeStage;
        this.setState(s);
    }

    private LifeStageAgeChange(value: string): void {
        const s = new MedicalItemsFamilyHistoryViewState(this.state);
        s.LifeStageAgeAtOnset = PatientMedicalItemsHelper.parseLifeStageAgeAtOnsetValue(value);
        s.LifeStageMode = LifeStageModeEnum.Age;

        this.setState(s);
    }

    private LifeStageAgeUnitChange(lookupId: string): void {
        const s = new MedicalItemsFamilyHistoryViewState(this.state);
        s.LifeStageAgeSelectedgeAtOnsetUnit = lookupId;
        s.LifeStageMode = LifeStageModeEnum.Age;
        this.setState(s);
    }

    private async Save(isSavePlus: boolean = false): Promise<void> {
        const medicalItems = this.props.Model.get("MedicalItemsList").toArray();
        let editMedicalItem = medicalItems.filter(x => x.IsCommitted == false).firstOrDefault() as MedicalItemFamilyHistory;
        editMedicalItem = MyleHelper.CloneObject(editMedicalItem);

        const medicalItemsToSend: MedicalItemFamilyHistory[] = [];

        if (editMedicalItem != null) {
            editMedicalItem.LifeStageAge = this.state.LifeStageAgeAtOnset;
            editMedicalItem.LifeStageAgeStatusLookupId = this.state.LifeStageAgeSelectedgeAtOnsetUnit;
            editMedicalItem.LifeStageDate = cacheStore.Helper.CreateDateAsUTC(this.state.LifeStageStartDate);
            editMedicalItem.LifeStageMode = this.state.LifeStageMode;
            editMedicalItem.LifeStagePrecisionDate = this.state.LifeStageDatePrecision;
            editMedicalItem.LifeStageStatusLookupId = this.state.LifeStageSelectedLifestageLookupId;
            editMedicalItem.Notes = this.state.Notes;

            if (!editMedicalItem.IsNewEMRTemplate) {
                editMedicalItem.Problem = this.state.Diagnostic;
                editMedicalItem.Description = this.state.Description;
                editMedicalItem.Treatment = this.state.Treatment;
                editMedicalItem.SpecifiedRelationship = this.state.OtherRelationshipSpecification;
                editMedicalItem.RelationshipLookupId = this.state.SelectedRelationshipLookupId;

                medicalItemsToSend.push(editMedicalItem);
            }
            else if (this.state.QualifiedDetails.length > 0) { // for other diagnostics/qualified details, clone first object and set corrresponding object
                for (let detail of this.state.QualifiedDetails) {
                    if (detail != null && detail.Text != null && detail.Text.trim().length > 0) {
                        const batchMedicalItem: MedicalItemFamilyHistory = JSON.parse(JSON.stringify(editMedicalItem));

                        batchMedicalItem.EMRTemplateId = GuidHelper.NewGuid();
                        batchMedicalItem.MedicalItemId = GuidHelper.NewGuid();
                        batchMedicalItem.StartDateLifeStageId = GuidHelper.NewGuid();

                        batchMedicalItem.Problem = detail.Text;
                        batchMedicalItem.Description = this.state.Description;
                        batchMedicalItem.Treatment = this.state.Treatment;
                        batchMedicalItem.SpecifiedRelationship = this.state.OtherRelationshipSpecification;
                        batchMedicalItem.RelationshipLookupId = this.state.SelectedRelationshipLookupId;

                        medicalItemsToSend.push(batchMedicalItem);
                    }
                }
            }
        }

        if (patientStore.IsMedicalItemFromOutsideNote) {
            await PatientMedicalItemsHelper.setOutsideNoteEncounter(medicalItemsToSend);
        }

        sessionStore.dispatch(
            LocalPatientOperations.MedicalItems.Save,
            {
                isSavePlus,
                view: MedicalItemsTab.FamilyHistory,
                editedItems: medicalItemsToSend
            }
        );
    }

    private async SavePlus(): Promise<void> {
        await this.Save(true);
    }

    private CheckIfSaveIsEnabled(): boolean {
        let isSaveEnabled = false;

        if (this.state.IsNewEMRTemplate) {
            isSaveEnabled = this.state.QualifiedDetails != null &&
                this.state.QualifiedDetails.length > 0 &&
                this.state.QualifiedDetails.some(x => x.Text != null && x.Text.trim().length > 0);
        }
        else {
            isSaveEnabled = this.state.Diagnostic != null && this.state.Diagnostic.trim().length > 0;
        }

        return isSaveEnabled;
    }

    private searchMedicalItem(items: MedicalItem[]): MedicalItemFamilyHistory[] {
        let self = this;
        let medicalItems: MedicalItemFamilyHistory[] = items as MedicalItemFamilyHistory[]; // cast MedicalItem to family history

        return medicalItems.filterSearch(self.props.Model.get("SearchTerm"), item => {
            // items cannot be null for search, replace them with empty string
            let startDate = MyleHelper.replaceNullWithEmptyString(item.PharmaStartDate);
            let problem = MyleHelper.replaceNullWithEmptyString(item.Problem);

            let lifeStageAge = MyleHelper.replaceNullWithEmptyString(item.LifeStageAge);
            let lifeStageDate = MyleHelper.replaceNullWithEmptyString(item.LifeStageDate);
            let lifeStageStatusLookup = (cacheStore.GetLookupById(item.LifeStageStatusLookupId) != null) ? cacheStore.GetLookupById(item.LifeStageStatusLookupId).GetValue() : "";
            let relationship = (cacheStore.GetLookupById(item.RelationshipLookupId) != null) ? cacheStore.GetLookupById(item.RelationshipLookupId).GetValue() : "";

            return startDate + " " + problem + " " + lifeStageAge + " " + lifeStageDate + " " + lifeStageStatusLookup + " " + relationship;
        });
    }
}

export { MedicalItemsFamilyHistoryView };
